import { useState } from 'react';

export default function FooterSection() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch('https://readdy.ai/api/form/d6f4e02ohb161tfd9di0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch (error) {
      console.error('Newsletter submission failed:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#2d3e40] pt-12 md:pt-20 pb-6 md:pb-8">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        {/* Top Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 pb-10 md:pb-16 border-b border-white/10">
          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <span className="font-playfair text-[24px] md:text-[28px] font-bold text-white">
              Marco Incitti
            </span>
            <p className="font-montserrat text-[12px] md:text-[13px] text-white/40 mt-1 mb-4 md:mb-6">
              Architetto Ecologico
            </p>
            <p className="font-playfair italic text-[16px] md:text-[18px] text-white/60 leading-[1.6] mb-6 md:mb-8">
              &ldquo;Sostenibilità e Visione.<br />Roma, Italia.&rdquo;
            </p>

            {/* Newsletter */}
            {!submitted ? (
              <form
                id="newsletter-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex gap-2"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="La tua email"
                  required
                  className="flex-1 min-w-0 bg-white/5 border border-white/15 rounded-lg px-4 py-3 font-montserrat text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#6b8e7f] transition-colors"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#6b8e7f] text-white font-montserrat text-[11px] md:text-[12px] uppercase tracking-[1px] px-4 md:px-5 py-3 rounded-lg hover:bg-[#5a7d6e] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {submitting ? '...' : 'Iscriviti'}
                </button>
              </form>
            ) : (
              <div className="bg-[#6b8e7f]/20 border border-[#6b8e7f]/30 rounded-lg px-4 py-3">
                <p className="font-montserrat text-[13px] text-[#8fb5a3]">
                  <i className="ri-check-line mr-2"></i>Grazie per l&apos;iscrizione!
                </p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-montserrat text-[11px] uppercase tracking-[2px] text-[#6b8e7f] mb-4 md:mb-6">
              <a href="#home">Navigazione</a>
            </h4>
            <div className="flex flex-col gap-2.5 md:gap-3">
              {[
                { id: 'home', label: 'Home' },
                { id: 'filosofia', label: 'Filosofia' },
                { id: 'storia', label: 'La Mia Storia' },
                { id: 'progetti', label: 'Progetti' },
                { id: 'passione', label: 'Passione' },
                { id: 'visione', label: 'Visione del Futuro' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="font-montserrat text-[13px] text-white/50 hover:text-[#6b8e7f] transition-colors cursor-pointer text-left whitespace-nowrap"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-montserrat text-[11px] uppercase tracking-[2px] text-[#6b8e7f] mb-4 md:mb-6">
              <a href="#progetti">Servizi</a>
            </h4>
            <div className="flex flex-col gap-2.5 md:gap-3">
              {[
                'Progettazione Sostenibile',
                'Riqualificazione Urbana',
                'Consulenza Energetica',
                'Bio-Edilizia',
                'Certificazioni Ambientali',
              ].map((item, i) => (
                <span key={i} className="font-montserrat text-[13px] text-white/50">
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-montserrat text-[11px] uppercase tracking-[2px] text-[#6b8e7f] mb-4 md:mb-6">
              <a href="mailto:studio@marcoincitti.com">Contatto</a>
            </h4>
            <div className="flex flex-col gap-3 md:gap-4">
              <a
                href="mailto:studio@marcoincitti.com"
                className="font-montserrat text-[13px] md:text-[14px] text-white/70 hover:text-[#6b8e7f] transition-colors flex items-center gap-3 cursor-pointer"
              >
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-[#6b8e7f] text-sm"></i>
                </div>
                studio@marcoincitti.com
              </a>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                  <i className="ri-map-pin-line text-[#6b8e7f] text-sm"></i>
                </div>
                <span className="font-montserrat text-[13px] md:text-[14px] text-white/70">
                  Roma, Italia
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-5 md:pt-6">
          <p className="font-montserrat text-[10px] md:text-[11px] text-white/30 text-center sm:text-left">
            Copyright &copy; 2026 Marco Incitti. Progettazione Bio-Architetturale.
            Tutti i diritti riservati.
          </p>
        </div>
      </div>
    </footer>
  );
}
